import { useState, useMemo, useCallback } from 'react';
import { MaterialData } from '../../../../../../types/materials';

interface ForgeFilters {
  status: string[];
  nri: string[];
  orderNumber: string[];
  serialNumber: string[];
  heatNumber: string[];
  forgeNRI: string[];
  forgeProjectStage: string[];
}

interface UseForgeFiltersProps {
  materials: MaterialData[];
}

export const useForgeFilters = ({ materials }: UseForgeFiltersProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<ForgeFilters>({
    status: [],
    nri: [],
    orderNumber: [],
    serialNumber: [],
    heatNumber: [],
    forgeNRI: [],
    forgeProjectStage: [],
  });

  // Extract unique NRI numbers from materials
  const uniqueNRIs = useMemo(() => {
    const nris = new Set<string>();
    materials.forEach((material: MaterialData) => {
      if (material.details?.forgeNRI) {
        nris.add(material.details.forgeNRI);
      }
    });
    return Array.from(nris).sort();
  }, [materials]);

  // Extract unique order numbers from materials
  const uniqueOrderNumbers = useMemo(() => {
    const orderNumbers = new Set<string>();
    materials.forEach((material: MaterialData) => {
      if (material.details?.forgeOrderNumber) {
        orderNumbers.add(material.details.forgeOrderNumber);
      }
    });
    return Array.from(orderNumbers).sort();
  }, [materials]);

  // Extract unique serial numbers from materials
  const uniqueSerialNumbers = useMemo(() => {
    const serialNumbers = new Set<string>();
    materials.forEach((material: MaterialData) => {
      if (material.details?.forgeSerialNumber) {
        serialNumbers.add(material.details.forgeSerialNumber);
      }
    });
    return Array.from(serialNumbers).sort();
  }, [materials]);

  // Extract unique heat numbers from materials
  const uniqueHeatNumbers = useMemo(() => {
    const heatNumbers = new Set<string>();
    materials.forEach((material: MaterialData) => {
      if (material.details?.forgeHeatNumber) {
        heatNumbers.add(material.details.forgeHeatNumber);
      }
    });
    return Array.from(heatNumbers).sort();
  }, [materials]);

  // Extract unique forge NRIs from materials
  const uniqueForgeNRIs = useMemo(() => {
    const forgeNRIs = new Set<string>();
    materials.forEach((material: MaterialData) => {
      if (material.details?.forgeNRI) {
        forgeNRIs.add(material.details.forgeNRI);
      }
    });
    return Array.from(forgeNRIs).sort();
  }, [materials]);

  // Extract unique forge project stages from materials
  const uniqueForgeProjectStages = useMemo(() => {
    const forgeProjectStages = new Set<string>();
    materials.forEach((material: MaterialData) => {
      if (material.details?.forgeProjectStage) {
        forgeProjectStages.add(material.details.forgeProjectStage);
      }
    });
    return Array.from(forgeProjectStages).sort();
  }, [materials]);

  // Filter materials based on search query and filters
  const filteredMaterials = useMemo(() => {
    return materials.filter((material: MaterialData) => {
      // Search query filter
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch = !searchQuery || 
        material.materialId?.toString().includes(searchLower) ||
        material.details?.forgeNRI?.toLowerCase().includes(searchLower) ||
        material.details?.forgeOrderNumber?.toLowerCase().includes(searchLower) ||
        material.details?.forgeSerialNumber?.toLowerCase().includes(searchLower) ||
        material.details?.forgeHeatNumber?.toLowerCase().includes(searchLower) ||
        material.assemblyNumbers?.some((assembly: string) => assembly?.toLowerCase().includes(searchLower)) ||
        material.materialStatus?.toLowerCase().includes(searchLower);

      // Status filter
      const matchesStatus = filters.status.length === 0 || 
        filters.status.includes(material.materialStatus || '');

      // NRI filter
      const matchesNRI = filters.nri.length === 0 || 
        (material.details?.forgeNRI && filters.nri.includes(material.details.forgeNRI));

      // Order number filter
      const matchesOrderNumber = filters.orderNumber.length === 0 || 
        (material.details?.forgeOrderNumber && filters.orderNumber.includes(material.details.forgeOrderNumber));

      // Serial number filter
      const matchesSerialNumber = filters.serialNumber.length === 0 || 
        (material.details?.forgeSerialNumber && filters.serialNumber.includes(material.details.forgeSerialNumber));

      // Heat number filter
      const matchesHeatNumber = filters.heatNumber.length === 0 || 
        (material.details?.forgeHeatNumber && filters.heatNumber.includes(material.details.forgeHeatNumber));

      // Forge NRI filter
      const matchesForgeNRI = filters.forgeNRI.length === 0 || 
        (material.details?.forgeNRI && filters.forgeNRI.includes(material.details.forgeNRI));

      // Forge project stage filter
      const matchesForgeProjectStage = filters.forgeProjectStage.length === 0 || 
        (material.details?.forgeProjectStage && filters.forgeProjectStage.includes(material.details.forgeProjectStage));

      return matchesSearch && matchesStatus && matchesNRI && 
             matchesOrderNumber && matchesSerialNumber && matchesHeatNumber &&
             matchesForgeNRI && matchesForgeProjectStage;
    });
  }, [materials, searchQuery, filters]);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return searchQuery !== '' || 
           filters.status.length > 0 || 
           filters.nri.length > 0 ||
           filters.orderNumber.length > 0 ||
           filters.serialNumber.length > 0 ||
           filters.heatNumber.length > 0 ||
           filters.forgeNRI.length > 0 ||
           filters.forgeProjectStage.length > 0;
  }, [searchQuery, filters]);

  // Handle filter changes
  const onFilterChange = useCallback((key: keyof ForgeFilters) => (value: string[]) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  }, []);

  // Reset all filters
  const onResetFilters = useCallback(() => {
    setSearchQuery('');
    setFilters({
      status: [],
      nri: [],
      orderNumber: [],
      serialNumber: [],
      heatNumber: [],
      forgeNRI: [],
      forgeProjectStage: [],
    });
  }, []);

  return {
    searchQuery,
    setSearchQuery,
    filters,
    onFilterChange,
    uniqueNRIs,
    uniqueOrderNumbers,
    uniqueSerialNumbers,
    uniqueHeatNumbers,
    uniqueForgeNRIs,
    uniqueForgeProjectStages,
    filteredMaterials,
    hasActiveFilters,
    filteredCount: filteredMaterials.length,
    onResetFilters,
  };
}; 